import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';

import { components } from 'react-select';

import { Property } from './index.jsx';
import { Accordion, AccordionContext, Button, Col, Image, Modal, Row } from 'react-bootstrap';
import FontDialog from 'components/Font/upload.jsx';
import { colorToString } from 'components/Editor/helper/color.jsx';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import Tooltip from 'components/Tooltip/index.jsx';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faMinus,
  faAlignLeft, faAlignCenter, faAlignJustify, faAlignRight,
  faListOl, faListUl, faSlash, faFill, faTint, faUpload
} from '@fortawesome/free-solid-svg-icons';

import {themes as DEFAULT_THEMES} from 'constants/themes';

import styles from './properties.module.scss';

/**
 * Constants
 * 
*/
export const THEME_OPTIONS = [
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'h5', label: 'Heading 5' },
  { value: 'h6', label: 'Heading 6' },
  { value: 'p', label: 'Paragraph' }
];

export const BRANDKIT_THEME_OPTIONS = [
  { value: 'h1', label: 'H1', desktopFontSize: '67' },
  { value: 'h2', label: 'H2', desktopFontSize: '50' },
  { value: 'h3', label: 'H3', desktopFontSize: '37' },
  { value: 'h4', label: 'H4', desktopFontSize: '28' },
  { value: 'h5', label: 'H5', desktopFontSize: '21' },
  { value: 'h6', label: 'H6', desktopFontSize: '16' },
  { value: 'p', label: 'Paragraph', desktopFontSize: '16' }
];

const ALIGNMENT_OPTIONS = [
  { value: 'left', label: "Left" },
  { value: 'center', label: "Center" },
  { value: 'right', label: "Right" },
  { value: 'justify', label: "Justify" }
];

const BULLET_OPTIONS = [
  { value: 'none', label: "None" },
  { value: 'ol', label: "OL" },
  { value: 'ul', label: "UL" }
];

/**
 * Custom Components
 */

const ThemeDropdownOption = (props) => {

  const fontSize = _.get(props, 'data.font.fontSize');
  const fontColor = _.get(props, 'data.font.color');

  const textStyles = {
    fontFamily: _.get(props, 'data.font.fontFamily', "Montserrat,sans-serif"),
    fontWeight: _.get(props.data, 'font.style.bold', false) ? 700 : 500,
    fontStyle: _.get(props.data, 'font.style.italic', false) ? 'italic' : 'normal'
  }

  return (
    <components.Option {...props} className={styles.fontThemeOption}>
      {fontSize && fontColor &&
        <div className={styles.colorSwatchWrapper}>
          <div style={{ backgroundColor: colorToString(fontColor) }} className={styles.colorSwatch} />
          <span className={styles.fontSize} >{`${fontSize}px`}</span>
        </div>}
      {React.createElement(props.data.value, { style: textStyles }, [props.children])}
    </components.Option>
  );
}

const ThemeMenu = (props) => {
  return (
    <components.Menu {...props} className={styles.fontThemeMenu}>
      {props.children}
    </components.Menu>
  );
}

const DropdownIndicator = props => {
  return (
    <components.DropdownIndicator {...props}>
      <i className={'cleverstory-drop-down'} />
    </components.DropdownIndicator>
  );
};

const FamilyDropdownOption = (props) => {
  Fonts.useFont([props.data.font]);

  return (
    <components.Option {...props} className={styles.fontFamilyOption}>
      <div style={{
        fontFamily: props.data.value,
      }}>{props.children}</div>
    </components.Option>
  );
}

const FamilyMenu = (props) => {
  const { onFontUpload } = props.selectProps;

  return (
    <components.Menu {...props} className={styles.fontFamilyMenu}>
      {props.children}
      <button className={styles.addFont} onClick={onFontUpload}>
        <i className='cleverstory-upload' /> Upload fonts
      </button>
    </components.Menu>
  );
}

const AlignmentDropdownOption = (props) => {
  const value = props.data.value;
  return (
    <components.Option {...props} className={styles.fontAlignmentOption}>
      {value === 'left' &&
        <i className='cleverstory-align-left' />
      }

      {value === 'center' &&
        <i className='cleverstory-align-center' />
      }

      {value === 'right' &&
        <i className='cleverstory-align-right' />
      }

      {value === 'justify' &&
        <i className='cleverstory-justify' />
      }
    </components.Option>
  );
}

const AlignmentSingleValue = (props) => {
  const value = props.data.value;
  return (
    <components.SingleValue {...props} className={styles.fontAlignmentOption}>
      {value === 'left' &&
        <i className={cx('cleverstory-align-left')} />
      }

      {value === 'center' &&
        <i className={cx('cleverstory-align-center')} />
      }

      {value === 'right' &&
        <i className={cx('cleverstory-align-right')} />
      }

      {value === 'justify' &&
        <i className={cx('cleverstory-justify')} />
      }
    </components.SingleValue>
  );
}

const BulletDropdownOption = (props) => {
  const value = props.data.value;
  return (
    <components.Option {...props} className={styles.fontBulletOption}>
      {value === 'none' &&
        <span className="fa-layers fa-fw">
          <FontAwesomeIcon icon={faListUl} />
          <FontAwesomeIcon icon={faSlash} color='red' />
        </span>
      }

      {value === 'ul' &&
        <i className='cleverstory-unordered-list' />
      }

      {value === 'ol' &&
        <i className='cleverstory-ordered-list' />
      }
    </components.Option>
  );
}

const BulletSingleValue = (props) => {
  const value = props.data.value;
  return (
    <components.SingleValue {...props} className={styles.fontBulletValue}>
      <i className='cleverstory-unordered-list' />
    </components.SingleValue>
  );
}

const BulletPlaceholder = (props) => {
  return (
    <components.Placeholder {...props} className={styles.fontBulletValue}>
      <i className='cleverstory-unordered-list' />
    </components.Placeholder>
  );
}

const Font = {};

/**
 * --------------------------------------
 * Font Theme
 * --------------------------------------
 */

const FontTheme = (props) => {

  const [changed, setChanged] = useState(false);
  const [modal, setModal] = useState(false);
  const isBatch = props?.isBatch || false;

  var optionData = (props.options || DEFAULT_THEMES).map(item => {
    return {
      value: item.type,
      label: item.label,
      font: item.font
    }
  });

  const fontOption = _.size(props.options) > 0 ? optionData : THEME_OPTIONS;

  const theme = props.value || {};
  const fontFormat = isBatch ? (_.get(theme, 'value') || "p") : (_.get(theme, 'type') || 'p');

  useEffect(() => {
    const savedTheme = (props.options || DEFAULT_THEMES).find(item => {
      return _.get(item, 'type') === fontFormat;
    });

    const oldTheme = _.get(savedTheme, 'font');
    const newTheme = _.get(theme, 'font');

    const themeChanged = !_.isEqual(oldTheme, newTheme);
    setChanged(themeChanged);

  }, [theme, props.options]);

  const handleSave = () => {
    setModal(true);
  }

  return (
    <>
      {!isBatch && 
        <div className={styles.themeButtonContainer}>
          <div className={styles.buttons} >
            {changed &&
              <button className={styles.save} onClick={handleSave}>Update Theme</button>
            }
            <Tooltip placement="top" content="Reset">
              <button className={styles.reset}  onMouseDown={(e) => e.preventDefault()} onClick={()=>{props.onReset(theme)}}>
                <i className={'cleverstory-rotate-left'} />
              </button>
            </Tooltip>
          </div>
        </div>
      }
      <Property name="Themes" className={styles.themeProperty}>
        <Property.Dropdown
          searchable={false}
          options={fontOption}
          components={{
            Option: ThemeDropdownOption,
            Menu: ThemeMenu,
            DropdownIndicator: DropdownIndicator
          }}
          value={fontFormat}
          onChange={props.onChange}
        />
        <ThemeSaveModal show={modal} 
          onConfirm={() => {
            props.onSave(theme);
            setModal(false);
          }}
          onCancel={() => {
            setModal(false);
          }}
        />
      </Property>
    </>
  );
}

const ThemeSaveModal = ({ show, onConfirm, onCancel }) => {
  return (
    <Modal backdrop='static'
      className={styles.themeSaveModal}
      contentClassName={styles.themeUpdateContent}
      dialogClassName={styles.themeUpdateDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <Modal.Body className={styles.themeUpdateBody}>
        <Button onClick={onCancel} className={styles.closeButton} variant="info" >
          <i className={'cleverstory-close'} />
        </Button>
        <Image src={'/images/themeUpdate.png'} className={styles.icon} />
        <div className={styles.details}>
          <h2>Update <br></br> Text Theme</h2>
          <p>
            The new theme for Style name will be applied to all existing and new text elements using this style.
          </p>
          <div className={styles.actions}>
            <Button className={styles.cancelButton} onClick={onCancel} variant="secondary">Cancel</Button>
            <Button className={styles.applyButton} onClick={onConfirm} variant="primary">Apply</Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

Font.Theme = FontTheme;

/**
 * --------------------------------------
 * Font Family
 * --------------------------------------
 */

const FontFamily = (props) => {
  const selectRef = useRef(null);
  const availableFonts = useSelector(state => _.get(state, 'fonts.items'));
  const customFonts = useSelector(state => _.get(state, 'workspaces.workspace.item.customFonts', ''));
  const [fontOptions, setFontOptions] = useState([]);
  const [uploadFont, setUploadFont] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let fonts;
  
    if (customFonts) {
      fonts = availableFonts
        .filter(font => !font.systemDefined)
        .map(font => ({
          label: font.name,
          value: font.fontFamily,
          font: font
        }));
    } else {
      fonts = availableFonts.map(font => ({
        label: font.name,
        value: font.fontFamily,
        font: font
      }));
    }

    fonts = _.orderBy(fonts, 'label', 'asc');
    setFontOptions(fonts);
    
  }, [availableFonts]);

  const handleInputChange = (value) => {
    setQuery(value);

    const dropdownMenu = selectRef.current.menuListRef;
    if (dropdownMenu) {
      dropdownMenu.scrollTop = 0;
    }
  }

  const filteredOptions = (options, query) => {
    const input = query.toLowerCase();

    if (!options) {
      return
    }

    let startingLettersMatch = options.filter(option => {
      const label = option.label ? option.label.toLowerCase() : '';
      return label.startsWith(input);
    });

    let includesLettersMatch = options.filter(option => {
      const label = option.label ? option.label.toLowerCase() : '';
      return label.includes(input) && !label.startsWith(input);
    });

    return [...startingLettersMatch, ...includesLettersMatch];
  };

  return (
    <Property name={_.get(props, "name", "Font")} className={props.className}>
      <Property.Dropdown
        className={styles.fontFamily}
        searchable={true}
        options={filteredOptions(fontOptions, query)}
        onInputChange={handleInputChange}
        components={{
          Option: FamilyDropdownOption,
          Menu: FamilyMenu,
          DropdownIndicator: DropdownIndicator
        }}
        value={props.value}
        onChange={props.onChange}
        onFontUpload={() => {
          setUploadFont(true);
        }}
        ref={selectRef}
      />
      <FontDialog show={uploadFont}
        onClose={() => {
          setUploadFont(false);
        }}
      />
    </Property>
  );
}

Font.Family = FontFamily;

/**
 * --------------------------------------
 * Font Size
 * --------------------------------------
 */

const FontSize = (props) => {
  const name = props.name || "Size";
  const min = props.min || 6;
  const max = props.max || (props.breakpoint === "mobile" ? 150 : 300);
  return (
    <Property name={name} className={props.className}>
      <Property.Slider
        input
        min={min}
        max={max}
        step={1}
        value={props.value}
        onChange={props.onChange}
      />
    </Property>
  );
}

Font.Size = FontSize;

/**
 * --------------------------------------
 * Font Spacing
 * --------------------------------------
 */

function CustomAccordianHeader({ children, eventKey, callback }) {
  const { activeEventKey } = React.useContext(AccordionContext);
  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <div className={styles.header}>
      {!isCurrentEventKey &&
        <i className={cx(styles.plusIcon, 'cleverstory-plus')} />
      }

      {isCurrentEventKey &&
        <i className={cx(styles.minusIcon, 'cleverstory-minus')} />
      }
      <span>{children}</span>
    </div>
  );
}


const FontSpacing = (props) => {
  const lineSpacing = _.get(props.value, 'lineSpacing');
  const letterSpacing = _.get(props.value, 'letterSpacing');

  const handleChange = (field, value) => {
    const request = {
      ...props.value,
      [field]: value
    }

    if (props.onChange) {
      props.onChange(request);
    }
  }

  return (
    <Accordion className={styles.fontSpacing} flush >
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          <CustomAccordianHeader eventKey='0'>Line and Character Spacing</CustomAccordianHeader>
        </Accordion.Header>
        <Accordion.Body>
          <Property name="Line Spacing">
            <Property.Slider
              input
              min={0.5}
              max={3.0}
              step={0.1}
              value={lineSpacing}
              onChange={(value) => {
                handleChange('lineSpacing', value);
              }}
            />
          </Property>
          <Property name="Character Spacing">
            <Property.Slider
              input
              min={-0.1}
              max={0.7}
              step={0.05}
              value={letterSpacing}
              onChange={(value) => {
                handleChange('letterSpacing', value);
              }}
            />

          </Property>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

Font.Spacing = FontSpacing;

/**
 * --------------------------------------
 * Font Alignment
 * --------------------------------------
 */

const FontAlignment = ({ value, onChange }) => {

  const handleAlignmentChange = (position) => {
    var request = {
      value: position
    }

    onChange(request);
  }

  const renderName = (item) => {
    if (item.value){
      const itemName = `${item.value.charAt(0).toUpperCase()}${item.value.slice(1)}`;
      return itemName === "Justify" ? itemName : `${itemName} Align`;
    }
    return '';
  }

  return (
    <div className={styles.textAlignment}>
      <div className={styles.options}>
        {
          ALIGNMENT_OPTIONS.map((item, index) => (
            <Tooltip placement="top" content={renderName(item)}>
              <button key={index} className={cx(styles.alignment,
                {
                  [styles.active]: value === item.value
                })}
                onClick={handleAlignmentChange.bind(this, item.value)}
              > 
                <i className={cx(`cleverstory-align-${item.value}`)}></i>
              </button>
            </Tooltip>
          ))
        }
      </div>
    </div>
  );
}

Font.Alignment = FontAlignment;

/**
 * --------------------------------------
 * Font Alignment
 * --------------------------------------
 */
const FontBullets = ({ value, onChange }) => {

  const handleBulletsChange = (bullets) => {
    if (bullets !== value) {
      var request = {
        value: bullets
      }
      onChange(request);
    }
    else {
      onChange({ value: 'none' });
    }
  }
  return (
    <div className={styles.textAlignment}>
      <div className={styles.options}>
        <Tooltip placement="top" content="Bulleted List">
          <button className={cx(styles.alignment,
            {
              [styles.active]: value === 'ul'
            })}
            onClick={handleBulletsChange.bind(this, 'ul')}
          >
            <i className={cx('cleverstory-unordered-list')}></i>
          </button>
        </Tooltip>
        <Tooltip placement="top" content="Numbered List">
          <button className={cx(styles.alignment,
            {
              [styles.active]: value === 'ol'
            })}
            onClick={handleBulletsChange.bind(this, 'ol')}
          >
            <i className={cx('cleverstory-ordered-list')}></i>
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

Font.Bullets = FontBullets;

/**
 * --------------------------------------
 * Font Color
 * --------------------------------------
 */

const FontColor = (props) => {
  return (
    <Property.ColorButton
      trigger={
        <div>
          <Tooltip placement="top" content="Text Color">
            <button className={styles.fontColor}>
              <i className={'cleverstory-font-color'} />
            </button>
          </Tooltip>
        </div>
      }
      value={props.value}
      onChange={props.onChange}
    />
  );
}

function fontColorEqual(prevProps, nextProps) {
  const paths = ['value', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}


Font.Color = React.memo(FontColor, fontColorEqual);;

/**
 * --------------------------------------
 * Font Background Color
 * --------------------------------------
 */

const FontBackground = (props) => {
  return (
    <Property.ColorButton
      trigger={
        <div>
          <Tooltip placement="top" content="Highlight">
            <button className={styles.fontColor}>
              <i className={'cleverstory-fill-color'} />
            </button>
          </Tooltip>
        </div>
      }
      clearable={true}
      value={props.value}
      onChange={props.onChange}
    />
  );
}

function fontBackgroundEqual(prevProps, nextProps) {
  const paths = ['value', 'onChange'];

  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

Font.Background = React.memo(FontBackground, fontBackgroundEqual);

export { Font };